import { useState, useCallback, useEffect } from 'react';
import { useNotificationProvider } from 'hooks';
import { CANtrackComment, CANtrackCase } from 'containers/pages/comments/types/comment';
import { convertToCANtrackComment } from 'containers/pages/comments/utils/convertToCANtrackComment';
import getCommentsByCaseId from 'api/cases/getCommentsByCaseId';

interface UseCaseCommentsProps {
	caseId: number;
	caseTitle: string;
	customerId: number;
	customerName: string;
	groupId: number;
	groupTitle: string;
	projectId: number;
	projectTitle: string;
	deliverableId: number;
	deliverableTitle: string;
}

function useCaseComments(props: UseCaseCommentsProps) {
	const {
		caseId,
		caseTitle,
		customerId,
		customerName,
		groupId,
		groupTitle,
		projectId,
		projectTitle,
		deliverableId,
		deliverableTitle,
	} = props;

	const [comments, setComments] = useState<CANtrackComment[]>([]);
	const [loadingComments, setLoadingComments] = useState(false);
	const [commentToCopy, setCommentToCopy] = useState<CANtrackComment | undefined>(undefined);
	const { notifyError } = useNotificationProvider();

	const defaultCaseContext: CANtrackCase = {
		id: caseId,
		title: caseTitle,
		description: '',
		consultantID: 0,
		consultantName: '',
		customerName,
		customerID: customerId,
		gpd: {
			group: { id: groupId, title: groupTitle },
			project: { id: projectId, title: projectTitle },
			deliverable: { id: deliverableId, title: deliverableTitle },
		},
	};

	const fetchComments = useCallback(async () => {
		if (!caseId) return;
		setLoadingComments(true);
		try {
			const rawComments = await getCommentsByCaseId(caseId);
			const converted = rawComments.map((raw) => convertToCANtrackComment(raw));
			setComments(converted);
		} catch (error) {
			console.error('[useCaseComments] Failed to fetch comments:', error);
			notifyError('Failed to fetch case comments');
		} finally {
			setLoadingComments(false);
		}
	}, [caseId, notifyError]);

	useEffect(() => {
		fetchComments();
	}, [fetchComments]);

	const handleCopyComment = useCallback((comment: CANtrackComment) => {
		setCommentToCopy(comment);
	}, []);

	const resetCommentToCopy = useCallback(() => {
		setCommentToCopy(undefined);
	}, []);

	const state = {
		comments,
		loadingComments,
		commentToCopy,
		defaultCaseContext,
	};

	const handler = {
		setComments,
		handleCopyComment,
		resetCommentToCopy,
		fetchComments,
	};

	return { state, handler };
}

export default useCaseComments;
